import React, { memo, ReactNode } from "react";

import { Modal } from "@UI/Components/Modal/Modal";
import { Button } from "@UI/Components/Button/Button";
import { Text } from "@UI/Components/Text/Text";
import { FormattedMessage } from "@I18n/Components/FormattedMessage/FormattedMessage";

import {
  TaskCreationSummary,
  ITaskCreationSummaryCategory,
  ITaskCreationSummaryStep,
} from "../TaskCreationSummary/TaskCreationSummary";
import messages from "../../messages";

export interface ITaskCreationResumeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onContinue: () => void;
  onDiscard: () => void;
  /** Absent for the leave-guard variant (RT-4321), which has no draft to show
   * a category pill for. */
  category?: ITaskCreationSummaryCategory;
  steps: ITaskCreationSummaryStep[];
  /**
   * Copy overrides so the leave-guard variant (RT-4321) can drive this same
   * modal with its own wording instead of forking the component. Default to
   * the resume copy, so the existing story and usage are unaffected.
   */
  title?: ReactNode;
  body?: ReactNode;
  continueLabel?: ReactNode;
  discardLabel?: ReactNode;
}

/**
 * Prompts a returning user with a saved draft to continue or discard it
 * (RT-4355). Reuses `TaskCreationSummary` for the category + step rows, with
 * its title overridden to the resume question and the two CTAs passed as its
 * footer slot — the modal owns the CTAs, the content stays container-agnostic.
 */
export const TaskCreationResumeModal = memo(
  ({
    isOpen,
    onClose,
    onContinue,
    onDiscard,
    category,
    steps,
    title,
    body,
    continueLabel,
    discardLabel,
  }: ITaskCreationResumeModalProps) => (
    <Modal isOpen={isOpen} onClose={onClose}>
      <TaskCreationSummary
        title={
          title ?? (
            <FormattedMessage {...messages["taskCreation.resume.title"]} />
          )
        }
        category={category}
        steps={steps}
      >
        {body && (
          <Text fontSize="sm" color="gray.600" marginBottom="16px">
            {body}
          </Text>
        )}
        <Button
          width="100%"
          colorScheme="seaGreen"
          marginBottom="8px"
          onClick={onContinue}
        >
          {continueLabel ?? (
            <FormattedMessage {...messages["taskCreation.resume.continue"]} />
          )}
        </Button>
        <Button
          width="100%"
          variant="outline"
          colorScheme="red"
          onClick={onDiscard}
        >
          {discardLabel ?? (
            <FormattedMessage {...messages["taskCreation.resume.discard"]} />
          )}
        </Button>
      </TaskCreationSummary>
    </Modal>
  )
);

TaskCreationResumeModal.displayName = "TaskCreationResumeModal";
